import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
// Configures Amplify Auth for its side effect. Must run before UserProvider
// mounts and calls fetchAuthSession().
import './authentication/amplifyConfig';
import i18n, { INITIAL_LOCALE, loadInitialLocale } from './i18n';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { canHydrate, getPrerenderedPath } from './hydration';
import { preloadMarketingRoute } from './navigation/marketingRoutes';

declare global {
  interface Window {
    __PRERENDER_READY__?: boolean;
    /**
     * What language the first render actually used. Read by scripts/prerender.js,
     * which fails the build if it does not match the locale the URL asks for —
     * silently snapshotting English under a `/de` prefix is worse than no snapshot,
     * because it ships wrong content to users and to crawlers without breaking
     * anything visible.
     */
    __I18N_RENDERED__?: { language: string; hasCatalogue: boolean };
  }
}

// Suppress harmless ResizeObserver loop error (Cloudscape AppLayout + SplitPanel)
const origError = window.onerror;
window.onerror = (msg, ...args) => {
    if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) return true;
    return origError ? origError(msg, ...args) : false;
};
window.addEventListener('error', (e) => {
    if (e.message?.includes('ResizeObserver loop')) {
        e.stopImmediatePropagation();
    }
});

/*
 * Amplify's InvalidOriginException.
 *
 * In development this fires on ordinary page loads with no OAuth in flight, so it
 * was being swallowed to keep the console usable. The guard had no environment
 * check, so it swallowed the production case too — and the production case is not
 * noise. It is what Amplify raises when it declines to complete a
 * signInWithRedirect callback, which leaves the ?code= in the URL unexchanged and
 * the visitor looking signed out with nothing logged anywhere.
 *
 * Kept quiet in development, always reported in production.
 */
window.addEventListener('unhandledrejection', (e) => {
    if (e.reason?.name !== 'InvalidOriginException') return;
    if (process.env.NODE_ENV === 'development') {
        e.preventDefault();
        return;
    }
    // eslint-disable-next-line no-console
    console.error(
        '[auth] Amplify rejected the OAuth origin for ' +
            `"${window.location.origin}". A signInWithRedirect callback cannot complete, ` +
            'so the authorization code in the URL will not be exchanged for tokens. ' +
            'Check that this exact origin is listed in the Cognito app client callback ' +
            'URLs and in REACT_APP_REDIRECT_SIGN_IN.',
        e.reason
    );
});

const container = document.getElementById('root') as HTMLElement;

const tree = (
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

/**
 * A hydration mismatch is not fatal — React recovers by client-rendering the
 * offending subtree — but it silently gives back everything hydration bought, so
 * it needs to be loud rather than invisible.
 */
function onRecoverableError(error: unknown, errorInfo: { componentStack?: string }) {
  // eslint-disable-next-line no-console
  console.error(
    '[hydration] recovered from an error while adopting prerendered markup for ' +
      `"${window.location.pathname}". The page was rebuilt on the client, which ` +
      'undoes the prerender. Fix the render-time nondeterminism that caused it.',
    error,
    errorInfo?.componentStack ?? ''
  );
}

/*
 * Adopt the prerendered DOM when it belongs to this route.
 *
 * This used to be an unconditional createRoot(), which discards whatever is in the
 * container and rebuilds it. The prerendered page was therefore painted, thrown
 * away and re-created on every single load — visible as the page flashing and
 * re-laying-out once main.js finished executing.
 *
 * Falls back to createRoot for paths with no snapshot of their own (dashboard
 * routes, and anything CloudFront rewrites to the homepage), where the served
 * markup describes a different page and adopting it would be wrong.
 */
function mount() {
  if (canHydrate(container, window.location.pathname)) {
    ReactDOM.hydrateRoot(container, tree, { onRecoverableError });
  } else {
    if (process.env.NODE_ENV === 'development' && getPrerenderedPath(container)) {
      // eslint-disable-next-line no-console
      console.info(
        `[hydration] snapshot is for "${getPrerenderedPath(container)}" but the URL is ` +
          `"${window.location.pathname}" — client rendering instead.`
      );
    }
    ReactDOM.createRoot(container).render(tree);
  }
}

function signalPrerenderReady() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.__PRERENDER_READY__ = true;
    });
  });
}

/*
 * Load this route's code-split chunk, and this page's translations, before
 * rendering anything.
 *
 * Three things depend on this ordering. Hydration: a Suspense boundary that resolves
 * while React is adopting prerendered markup makes it throw that markup away, so
 * the chunk has to be in hand before the first render, not during it. The prerender
 * itself: scripts/prerender.js snapshots the DOM once __PRERENDER_READY__ is set, so
 * if that fired while a route was still suspended the snapshot would capture an empty
 * Suspense fallback and we would ship a blank page. Signalling readiness only after
 * mount() has been called keeps both honest.
 *
 * And now the catalogue: only en-US is bundled, so on a localized URL the first
 * render would otherwise fall back to English. On an ordinary load that is a flash of
 * the wrong language; during the prerender it is worse, because the snapshot would
 * freeze English into the HTML served for `/de`. Both are avoided by having the
 * messages in the store before React runs.
 *
 * Neither failure is fatal, so neither blocks mounting: a missed preload falls
 * through to React.lazy and the Suspense boundary in NavigationComponent, and a
 * missed catalogue falls through to i18next's en-US fallback. Both are logged, and
 * the prerender treats a missing catalogue as a build failure via __I18N_RENDERED__.
 */
Promise.all([
  preloadMarketingRoute(window.location.pathname).catch(error => {
    // eslint-disable-next-line no-console
    console.error(
      `[routes] failed to preload the chunk for "${window.location.pathname}"; ` +
        'falling back to suspending on first render.',
      error
    );
  }),
  loadInitialLocale().catch(error => {
    // eslint-disable-next-line no-console
    console.error(
      `[i18n] failed to load the "${INITIAL_LOCALE}" catalogue; this page will ` +
        'render in English.',
      error
    );
  }),
]).then(() => {
  window.__I18N_RENDERED__ = {
    language: i18n.language,
    hasCatalogue: i18n.hasResourceBundle(i18n.language, 'translation'),
  };
  mount();
  if (i18n.isInitialized) {
    signalPrerenderReady();
  } else {
    i18n.on('initialized', signalPrerenderReady);
  }
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
